'use client';

import { useEffect, useRef } from 'react';

import { IDLE_REFRESH_TIMEOUT_MS } from './shared-survey-idle-refresh.consts';
import { SURVEY_ACTIVITY_EVENT } from './shared-survey-idle-refresh.helpers';

const ACTIVITY_EVENTS: readonly string[] = [
  'mousemove',
  'mousedown',
  'keypress',
  'scroll',
  'touchstart',
  SURVEY_ACTIVITY_EVENT,
];

// Minimum interval between timer resets to avoid excessive setTimeout churn from mousemove
const THROTTLE_MS = 5 * 60 * 1000;

export function useIdleRefresh() {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastResetRef = useRef<number>(0);

  useEffect(() => {
    const resetTimer = () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
      lastResetRef.current = Date.now();
      timerRef.current = setTimeout(() => {
        window.location.reload();
      }, IDLE_REFRESH_TIMEOUT_MS);
    };

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastResetRef.current < THROTTLE_MS) return;
      resetTimer();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const elapsed = Date.now() - lastResetRef.current;
      if (elapsed >= IDLE_REFRESH_TIMEOUT_MS) {
        window.location.reload();
        return;
      }
      resetTimer();
    };

    resetTimer();

    ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
      ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
}
